
import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  SaveButton
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store';
import { createFacilityType, updateFacilityType } from '../../../../../modules/masterdata/actions';
import { IFacilityType } from '../../../../../modules/masterdata/types';
import { selectCreateFacilityTypeSuccess, selectUpdateFacilityTypeSuccess } from '../../../../../modules/masterdata/selectors';

interface FacilityTypeModalProps {
  open: boolean;
  onClose: () => void;
  type?: IFacilityType | null; // If provided, we are in Edit mode
}

const FacilityTypeModal: React.FC<FacilityTypeModalProps> = ({ open, onClose, type }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const isCreateSuccess = useSelector(selectCreateFacilityTypeSuccess);
  const isUpdateSuccess = useSelector(selectUpdateFacilityTypeSuccess);

  useEffect(() => {
    if (type) {
      setName(type.facility_type_name);
      setDescription(type.facility_type_description);
    } else {
      setName('');
      setDescription('');
    }
  }, [type, open]);

  useEffect(() => {
    if (isCreateSuccess) {
      onClose();
    }
    if (isUpdateSuccess) {
      onClose();
    }
  }, [isCreateSuccess, isUpdateSuccess, onClose]);

  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const handleSave = () => {
    const isNameValid = !!name.trim();
    const isDescriptionValid = !!description.trim();

    setNameError(!isNameValid);
    setDescriptionError(!isDescriptionValid);

    if (!isNameValid || !isDescriptionValid) {
      return;
    }

    if (type) {
      dispatch(updateFacilityType({
        id: type.facility_type_id,
        facility_type_name: name,
        facility_type_description: description
      }));
    } else {
      dispatch(createFacilityType({
        facility_type_name: name,
        facility_type_description: description
      }));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        {type ? 'Edit Facility Type' : 'Create Facility Type'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          required
          fullWidth
          value={name}
          error={nameError}
          helperText={nameError ? 'Name is required' : ''}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
        />
        <TextField
          margin="dense"
          label="Description"
          required
          fullWidth
          multiline
          rows={3}
          value={description}
          error={descriptionError}
          helperText={descriptionError ? 'Description is required' : ''}
          onChange={(e) => {
            setDescription(e.target.value);
            if (e.target.value.trim()) setDescriptionError(false);
          }}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disableElevation>
          {type ? 'Save Changes' : 'Create'}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default FacilityTypeModal;
